import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormGroup } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { Observable, Subscription } from 'rxjs';
import { AbstractOnboardingPageComponent } from 'src/app/core/abstracts/components/onboarding-page.abstract';
import { CompanyResultViewTypes } from 'src/app/core/enums/company-result-view.enum';
import { EventTypes } from 'src/app/core/enums/event.enum';
import { IPageContent } from 'src/app/core/interfaces/common/content/page-content.interface';
import { IMultiOptions } from 'src/app/core/interfaces/onboarding/business-profile/business-multi-options.interface';
import { IBusinessProfile } from 'src/app/core/interfaces/onboarding/business-profile/business-profile.interface';
import { ICompanyInfo } from 'src/app/core/interfaces/onboarding/business-profile/company-Info-model.interface';
import { ITransition } from 'src/app/core/interfaces/onboarding/page-state/transition.interface';
import { CompanySearch } from 'src/app/core/models/business-profile/companySearch.model';
import { Address } from 'src/app/core/models/common/address.model';
import { PageState } from 'src/app/core/models/page-state/page-state.model';
import { BusinessProfileService } from 'src/app/core/services/onboarding/business-profile.service';
import { EnrollmentExtendedService } from 'src/app/core/services/onboarding/enrollment-extended.service';
import { PageStateService } from 'src/app/core/services/onboarding/page-state.service';
import { IAppState } from 'src/app/core/store/common/interfaces/app-state.interface';
import { CompanySearchAction } from 'src/app/core/store/onboarding/actions/business-profile.actions';
import { destructSubscriptionArray } from 'src/app/core/ui-helpers/functions/destruct-subscription-array';
import { watchTabletThreshold } from 'src/app/core/ui-helpers/functions/watch-tablet-threshold';
import { ContentIDConstants } from './ContentIDConstants';

@Component({
  selector: 'app-confirm-details',
  templateUrl: './confirm-details.component.html',
  styleUrls: ['./confirm-details.component.scss'],
})
export class ConfirmDetailsComponent
  extends AbstractOnboardingPageComponent
  implements OnInit, OnDestroy
{
  isTablet: boolean;

  backState: PageState;
  nextState: PageState;
  skipState: PageState;
  homeState: PageState;
  bpEdit1: PageState;
  bpEdit2: PageState;
  bpEdit3: PageState;
  bpEdit4: PageState;
  bpEdit5: PageState;
  bpEdit6: PageState;
  bpEdit7: PageState;
  bpEdit8: PageState;
  bpEdit9: PageState;
  bpEdit10: PageState;
  bpEdit11: PageState;
  bpEdit12: PageState;
  bpEdit13: PageState;
  companyNotFound: PageState;
  soleProprietorship: PageState;
  notQualified: PageState;
  bpSummaryState: PageState;

  form: FormGroup;
  displayData: any = [];
  questionText = 'Review and Confirm Business Profile Details';
  properties$: Observable<any>;
  subscriptions: Subscription[] = [];
  payload: IBusinessProfile;

  constructor(
    private route: ActivatedRoute,
    private ps: PageStateService,
    private ees: EnrollmentExtendedService,
    private bf: BusinessProfileService,
    private store: Store<IAppState>,
    private router: Router
  ) {
    super();
    this.subscriptions.push(
      watchTabletThreshold((isTablet: boolean) => {
        this.isTablet = isTablet;
      })
    );
  }

  async ngOnInit(): Promise<void> {
    this.subscriptions.push(
      this.route.data.subscribe(async (data) => {
        const { transitions } = data['data'];
        const { contentDict } = data['data'];
        this.populatePageStates(transitions);
        this.populatePageContent(contentDict);
        this.subscriptions.push(
          this.route.queryParams.subscribe((params) => {
            this.ees.updateBusinessProfileStateId(
              params['stateId'],
              this.homeState.id
            );
            // Get and update payload with display data
            this.payload = this.bf.getPayload();
            this.buildDisplayPayload(transitions);
          })
        );
      })
    );
  }

  ngOnDestroy(): void {
    destructSubscriptionArray(this.subscriptions);
  }

  async submit() {
    const companyInfo = await this.bf.getBestMatch();
    if (
      companyInfo != null &&
      companyInfo.response === CompanyResultViewTypes.NoMatchFound
    ) {
      await Promise.allSettled([
        this.ees.updateBusinessProfileStateId(
          this.bpSummaryState.id,
          this.companyNotFound.id
        ),
        this.ees.completeBusinessProfile(),
      ]);
      this.router.navigate([this.companyNotFound.pageUrl], {
        queryParams: { stateId: this.companyNotFound.id },
      });
    } else if (
      companyInfo != null &&
      (companyInfo.response === CompanyResultViewTypes.SoleProprietorship ||
        companyInfo.response === CompanyResultViewTypes.WrongCustomers ||
        companyInfo.response === CompanyResultViewTypes.WrongLendingPractices ||
        companyInfo.response ===
          CompanyResultViewTypes.WrongInvoicingPractices ||
        companyInfo.response === CompanyResultViewTypes.WrongBusinessIndustry)
    ) {
      await Promise.allSettled([
        this.ees.updateBusinessProfileStateId(
          this.bpSummaryState.id,
          this.soleProprietorship.id
        ),
        this.ees.completeBusinessProfile(),
      ]);
      this.router.navigate([this.soleProprietorship.pageUrl], {
        queryParams: { stateId: this.soleProprietorship.id },
      });
    } else if (
      companyInfo != null &&
      companyInfo.response === CompanyResultViewTypes.NotQualified
    ) {
      await Promise.allSettled([
        this.ees.updateBusinessProfileStateId(
          this.bpSummaryState.id,
          this.notQualified.id
        ),
        this.ees.completeBusinessProfile(),
      ]);
      this.router.navigate([this.notQualified.pageUrl], {
        queryParams: { stateId: this.notQualified.id },
      });
    } else {
      this.dispatchCompanySearch(companyInfo);
      this.router.navigate([this.nextState.pageUrl], {
        queryParams: { stateId: this.nextState.id },
      });
    }
    return false;
  }
  populatePageStates(transitions: ITransition[]): void {
    this.backState = this.ps.getPageStateByEventAndTransitions(
      EventTypes.BACK,
      transitions
    );
    this.nextState = this.ps.getPageStateByEventAndTransitions(
      EventTypes.NEXT,
      transitions
    );
    this.skipState = this.ps.getPageStateByEventAndTransitions(
      EventTypes.SKIP,
      transitions
    );
    this.homeState = this.ps.getPageStateByEventAndTransitions(
      EventTypes.HOME,
      transitions
    );
    this.companyNotFound = this.ps.getPageStateByEventAndTransitions(
      EventTypes.COMPANY_NOT_FOUND,
      transitions
    );
    this.soleProprietorship = this.ps.getPageStateByEventAndTransitions(
      EventTypes.SOLE_PROPRIETORSHIP,
      transitions
    );
    this.notQualified = this.ps.getPageStateByEventAndTransitions(
      EventTypes.COMPANY_NOT_QUALIFIED,
      transitions
    );
    this.bpSummaryState = this.ps.getPageStateByEventAndTransitions(
      EventTypes.APPLICATION_SUMMARY,
      transitions
    );
  }

  populatePageContent(contentDict: Map<number, IPageContent>): void {
    this.questionText =
      contentDict.get(ContentIDConstants.Question)?.displayText ??
      this.questionText;
  }

  private buildDisplayPayload(transitions: ITransition[]) {
    const address = new Address();
    address.buildAddress({
      address1: this.payload.Address1,
      address2: this.payload.Address2,
      city: this.payload.City,
      state: this.payload.State,
      zipcode: this.payload.ZipCode,
    });

    this.displayData = [
      {
        key: 'Legal Form Of Business Name',
        value: this.payload.LegalFormOfBusinessName,
        editState: (this.bpEdit1 = this.ps.getPageStateByEventAndTransitions(
          EventTypes.BP_EDIT1,
          transitions
        )),
      },
      {
        key: 'Industry',
        value: this.payload.IndustryBusinessValue,
        editState: (this.bpEdit2 = this.ps.getPageStateByEventAndTransitions(
          EventTypes.BP_EDIT2,
          transitions
        )),
      },
      {
        key: 'Business Description',
        value: this.buildBusinessDescriptionString(
          this.payload.BusinessDescription
        ),
        editState: (this.bpEdit13 = this.ps.getPageStateByEventAndTransitions(
          EventTypes.BP_EDIT13,
          transitions
        )),
      },
      {
        key: 'Current Customers',
        value: this.buildCustomerTypesString(this.payload.CustomerTypes),
        editState: (this.bpEdit3 = this.ps.getPageStateByEventAndTransitions(
          EventTypes.BP_EDIT3,
          transitions
        )),
      },
      {
        key: 'State Of Organization',
        value: this.payload.StateOfOrganization,
        editState: (this.bpEdit4 = this.ps.getPageStateByEventAndTransitions(
          EventTypes.BP_EDIT4,
          transitions
        )),
      },
      {
        key: 'DBA',
        value: this.payload.DBA,
        editState: (this.bpEdit5 = this.ps.getPageStateByEventAndTransitions(
          EventTypes.BP_EDIT5,
          transitions
        )),
      },
      {
        key: 'Address',
        value: address.formatString(),
        editState: (this.bpEdit6 = this.ps.getPageStateByEventAndTransitions(
          EventTypes.BP_EDIT6,
          transitions
        )),
      },
      {
        key: 'Annual Revenue',
        value: this.payload.AnnualRevenue,
        editState: (this.bpEdit7 = this.ps.getPageStateByEventAndTransitions(
          EventTypes.BP_EDIT7,
          transitions
        )),
      },
      {
        key: 'Website',
        value: this.payload.Website,
        editState: (this.bpEdit8 = this.ps.getPageStateByEventAndTransitions(
          EventTypes.BP_EDIT8,
          transitions
        )),
      },
      {
        key: 'Year Founded',
        value: this.payload.YearFounded,
        editState: (this.bpEdit9 = this.ps.getPageStateByEventAndTransitions(
          EventTypes.BP_EDIT9,
          transitions
        )),
      },
      {
        key: 'Number Of Employees',
        value: this.payload.NumberOfEmployees,
        editState: (this.bpEdit10 = this.ps.getPageStateByEventAndTransitions(
          EventTypes.BP_EDIT10,
          transitions
        )),
      },
      {
        key: 'Invoicing Method',
        value: this.payload.InvoicingMethod,
        editState: (this.bpEdit11 = this.ps.getPageStateByEventAndTransitions(
          EventTypes.BP_EDIT11,
          transitions
        )),
      },
      {
        key: 'Type of Support Needed',
        value: this.buildSupportMethodsString(this.payload.SupportMethods),
        editState: (this.bpEdit12 = this.ps.getPageStateByEventAndTransitions(
          EventTypes.BP_EDIT12,
          transitions
        )),
      },
    ];
  }

  buildCustomerTypesString(customerTypes: IMultiOptions[]): string {
    const customerTypeValues = customerTypes.filter((x) => x.selected);
    const customerLen = customerTypeValues.length;
    let result = '';
    //loop through customerTypeValues and if customerTypeValues has value then take it as valueToUse orelse use customerTypeValues.name
    for (let i = 0; i < customerLen; i += 1) {
      const valueToUse = customerTypeValues[i].value !== '' ? customerTypeValues[i].value : customerTypeValues[i].name;
      if (i === customerLen - 1) {
        result += valueToUse;
      } else {
        result += `${valueToUse}, `;
      }
    }
    return result;
  }

  buildSupportMethodsString(supportMethods: IMultiOptions[]): string {
    const supportMethodValues = supportMethods.filter((x) => x.selected);
    const methodLen = supportMethodValues.length;
    let result = '';
    for (let i = 0; i < methodLen; i += 1) {
      if (i === methodLen - 1) {
        result += supportMethodValues[i].value;
      } else {
        result += `${supportMethodValues[i].value}, `;
      }
    }
    return result;
  }

  buildBusinessDescriptionString(businessDescription: string): string {
    if (businessDescription.length <= 25) return businessDescription;
    return `${businessDescription.substring(0, 22)}...`;
  }

  private dispatchCompanySearch(companyInfo: ICompanyInfo) {
    const companySearch = new CompanySearch();
    companySearch.registeredCompanyName =
      companyInfo.companySearchResultModel.registeredCompanyName;
    companySearch.address1 = companyInfo.companySearchResultModel.address1;
    companySearch.address2 = companyInfo.companySearchResultModel.address2;
    companySearch.state = companyInfo.companySearchResultModel.state;
    companySearch.city = companyInfo.companySearchResultModel.city;
    companySearch.zipCode = companyInfo.companySearchResultModel.zipCode;
    companySearch.lookupTypeId = companyInfo.lookupTypeId;
    companySearch.lookupId = companyInfo.lookupId;
    companySearch.entityNumber = companyInfo.entityNumber;

    // saving to store
    this.store.dispatch(
      CompanySearchAction({
        companySearch,
      })
    );
  }
}
